"use client";

import { useEffect, useMemo, useState } from "react";

import type { AuditionPayload } from "@/lib/music/analyzer-types";
import { MidiAuditionPlayer, type AuditionState } from "@/lib/midi/midi-audition-player";
import { getGmSoundfontSynth } from "@/lib/audio/gm-soundfont-synth";
import { useStudio } from "@/contexts/studio-context";

interface UseAuditionPlayerResult {
  /** Current playback state, kept in sync with the player's emitter. */
  state: AuditionState;
  /** Player instance, or `null` while the studio (and thus the AudioContext) is booting. */
  player: MidiAuditionPlayer | null;
}

const LOADING_STATE: AuditionState = { kind: "loading" };

/**
 * React-local lifecycle for {@link MidiAuditionPlayer}.
 *
 * The player is built lazily via `useMemo` (so we don't churn extra
 * instances on rerenders) and a colocated `useEffect` wires up the
 * `onStateChange` subscription and the dispose-on-unmount cleanup.
 *
 * The synth itself is an application singleton (`getGmSoundfontSynth`) so
 * samples loaded by a previous upload remain warm.
 */
export function useAuditionPlayer(
  audition: AuditionPayload | null,
): UseAuditionPlayerResult {
  const { studio } = useStudio();

  const player = useMemo<MidiAuditionPlayer | null>(() => {
    if (!studio || !audition) {
      return null;
    }

    const ctx = studio.getAudioContext();
    const synth = getGmSoundfontSynth(ctx);

    return new MidiAuditionPlayer(ctx, synth, audition.tracks);
  }, [studio, audition]);

  const [state, setState] = useState<AuditionState>(LOADING_STATE);

  useEffect(() => {
    if (!player) {
      return;
    }

    const unsubscribe = player.onStateChange((s) => {
      setState(s);
    });

    return () => {
      unsubscribe();
      player.dispose();
    };
  }, [player]);

  return { state, player };
}
